import { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { Spin, message } from 'antd';
import AIBeauty from './components/aiBeauty/aiBeauty'
import HandRepair from './components/handRepair'
import MagicErase from './components/magicErase'
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext } from '@utils/AppContext';
import FillLight from './components/fillLight/fillLight';
import ImageFiner from './components/imageFiner/imageFiner';
import Outpaint from './components/outpaint';
import EditLeftBar from './editLeftBar';
import Utils from '@utils/utils';
import API from '@api/api';
import eventBus from '@utils/eventBus';
import styles from './aiEdit.module.scss'
import { InfoCircleOutlined } from '@ant-design/icons';
import { AIEditContext, AIEditContextProvider } from './AIEditContext';
import PSAIModal from '@PSAIComponents/PSAIModal';
import WaitUploadPage from './components/WaitUploadPage2';
import Example from '@assets/json/examplev3.json'
import { HOME_PAGE_TYPE, IMAGE_TYPE, UPLOAD_SOURCE } from '@utils/CONST';
import RightComp from '@view/page_v3/RightComp'
import RightOperatePanel from './components/RightOperatePanel'
import BottomPlaceholder from './components/BottomPlaceholder'
import ResizePageRight from './imageResize/resizePageRight'
import { ImageResizeContextProvider } from './imageResize/imageResizeContext'

const AI_EDIT_UPLOAD_BG = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-web/admin/AIEdit/uploadBg/';

const AiEdit = () => {
    const {
        updateConfig,
        globalHomePageType,
        AIEditTemprojectId,
        setAIEditTempProjectId,
        setSelectedTaskListId,
        setGlobalTaskReadFlag,
        AIEditRecentStatus,
        setAIEditRecentTaskId } = useContext(AppContext)
    const { setAIEditUrl, setNeedPay, setAIEditLoading } = useContext(AIEditContext)

    const [exampleList] = useState(Example)

    const modelExamples = useMemo(() => {
        const imageType = globalHomePageType == HOME_PAGE_TYPE.MERCHANDISE ?
            IMAGE_TYPE.COMMODITY :
            IMAGE_TYPE.MODEL
        return exampleList
            .filter(item => item.type == imageType)
            .filter(item => !item.disabled)
    }, [exampleList, globalHomePageType])

    const location = useLocation();
    const query = new URLSearchParams(location.search)
    const queryMenuKey = query.get('menuKey')
    const queryImageUrl = query.get('url')
    const menuKeyRef = useRef('matting')
    const [menuKey, setMenuKey] = useState(location?.state?.menuKey || queryMenuKey || 'matting');
    const [loading, setLoading] = useState(false)
    const [bottomConfig, setBottomConfig] = useState(null)
    const imageUrlRef = useRef(location?.state?.picUrl || queryImageUrl)
    const projectIdRef = useRef(location?.state?.projectId)
    const taskIdRef = useRef(location?.state?.taskId)
    const imgMaxLimit = useRef(4096)
    const imgMinLimit = useRef(500)
    const [outputMask, setOutputMask] = useState()
    const [outpaintConfig, setOutpaintConfig] = useState({
        multiple: 125,
        ratio: '3:4',
        generateCount: 1,
        moveMode: true,
        resetSeed: 0,
    })
    const outpaintLayoutRef = useRef(null)

    const clearProjectAssociation = useCallback(() => {
        projectIdRef.current = null
        taskIdRef.current = null
        outpaintLayoutRef.current = null
        setAIEditTempProjectId()
        setSelectedTaskListId()
        setAIEditRecentTaskId()
    }, [setAIEditRecentTaskId, setAIEditTempProjectId, setSelectedTaskListId])

    useEffect(() => {
        updateConfig({ sidebarKey: 'aiEdit' })
    }, [])

    useEffect(() => {
        if (AIEditRecentStatus) {
            setAIEditLoading(true)
        }

    }, [AIEditRecentStatus])

    useEffect(() => {
        const stateImageUrl = location.state?.picUrl
        if (stateImageUrl || queryImageUrl) {
            imageUrlRef.current = stateImageUrl || queryImageUrl;
            setAIEditLoading(false)
        }
    }, [location.state?.picUrl, queryImageUrl]);

    useEffect(() => {
        const stateMenuKey = location.state?.menuKey
        const targetMenuKey = stateMenuKey || queryMenuKey
        if (targetMenuKey) {
            setMenuKey(targetMenuKey);
            menuKeyRef.current = targetMenuKey;
        }
        projectIdRef.current = location.state?.projectId || null
        taskIdRef.current = location.state?.taskId || null
        if (!location.state?.picUrl && !queryImageUrl) {
            imageUrlRef.current = null;
            projectIdRef.current = null
            taskIdRef.current = null
        }
    }, [location.state, queryMenuKey, queryImageUrl])

    const [MENU_ITEMS] = useState({
        MATTING: {
            label: '智能抠图',
            key: 'matting',
        },
        FILL_LIGHT: {
            label: '智能补光',
            key: 'filllight',
            icon: <i className={`${styles.LeftMenuIcon} ${styles.Icon_light}`}></i>
        },
        BEAUTIFY: {
            label: '模特美化',
            key: 'beautify',
            icon: <i className={`${styles.LeftMenuIcon} ${styles.Icon_beautify}`}></i>
        },
        MAGIC_ERASE: {
            label: '魔法擦除',
            key: 'magicErase',
            icon: <i className={`${styles.LeftMenuIcon} ${styles.Icon_magicErase}`}></i>
        },
        HAND_REPAIR: {
            label: '手部修复',
            key: 'handRepair',
            icon: <i className={`${styles.LeftMenuIcon} ${styles.Icon_handRepair}`}></i>
        },
        IMAGE_RESIZE: {
            key: 'imageResize',
        },
        IMAGE_FINER: {
            label: 'AI超清',
            key: 'imageFiner',
            icon: <i className={`${styles.LeftMenuIcon} ${styles.Icon_light}`}></i>
        },
        OUTPAINT: {
            label: 'AI扩图',
            key: 'outpaint',
            icon: <i className={`${styles.LeftMenuIcon} ${styles.Icon_imageUpgrade}`}></i>
        }
    })

    const filllightRef = useRef()
    const beautifyRef = useRef()
    const imageFinerRef = useRef()
    const magicEraseRef = useRef()
    const imageUpgradeRef = useRef()
    const handRepairRef = useRef()
    const imageResizeLeftRef = useRef()
    const rightMainRef = useRef()
    function isBase64(src) { return /^data:image\/[a-z]+;base64,/.test(src) }
    const onClick = async (e) => {
        const nextKey = e?.key
        if (!nextKey || nextKey === menuKeyRef.current) return

        // 切换模块时不继承上一模块的上传图/结果图，统一回到初始态
        imageUrlRef.current = null
        setAIEditUrl(null)
        setBottomConfig(null)
        setAIEditLoading(false)
        setOutpaintConfig({
            multiple: 125,
            ratio: '3:4',
            generateCount: 1,
            moveMode: true,
            resetSeed: Date.now(),
        })
        clearProjectAssociation()

        menuKeyRef.current = nextKey
        setMenuKey(nextKey)
    }

    const uploadConfirm = (type, { title, content, okText, cancelText, maskClosable, onOk = () => { }, onCancel = () => { } }) => {
        return new Promise((resolve) => {
            PSAIModal.confirm({
                title: title,
                content: content,
                closable: false,
                icon: <InfoCircleOutlined style={{ color: "#FF864C" }} />,
                centered: true,
                maskClosable: maskClosable,
                okText: okText,
                cancelText: cancelText,
                async onOk() {
                    try {
                        const res = await onOk()
                        resolve(res)
                    } catch (err) {
                        console.log('onOk reject', err)
                        resolve(false)
                        throw err
                    }
                },
                async onCancel() {
                    try {
                        const res = await onCancel()
                        console.log('onCancel', res)
                        resolve(res)
                    } catch (err) {
                        console.log('onCancel reject', err)
                        resolve(false)
                        throw err
                    }
                },
                async onClose() {
                    resolve(false)
                }
            })
        })
    }
    const errorCallback = () => {
        uploadConfirm('confirm', {
            title: '仅支持人像图片',
            content: '补光功能仅对人像生效',
            okText: '使用其他功能',
            cancelText: '重新上传',
            onOk: goToMagicErase,
            onCancel: reupload
        })
    }
    const errorCallback2 = () => {
        uploadConfirm('confirm', {
            title: '画质升级',
            content: '【画质升级】支持的最大尺寸为4096^4096',
            okText: '使用其他功能',
            cancelText: '重新上传',
            onOk: goToMagicErase,
            onCancel: reupload
        })
    }
    const reupload = () => {
        return new Promise((resolve, reject) => {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/jpeg,image/png,image/webp,image/avif'
            let flag = 0
            const onForcus = () => {
                setTimeout(() => {
                    if (flag == 0) {
                        reject(new Error('cancel'))
                    }
                    window.removeEventListener('focus', onForcus)
                }, 200)
            }
            window.addEventListener('focus', onForcus)
            input.addEventListener('change', () => {
                flag = 1
                const file = input.files[0];
                if (file) {
                    beforeUpload(file).then(() => resolve(true))
                    input.remove()
                } else {
                    reject(new Error('cancel'))
                }
            })
            input.click()
        })
    }
    const goToMagicErase = () => {
        menuKeyRef.current = 'magicErase'
        setMenuKey('magicErase')
        return false
    }
    const beforeUpload = async (file) => {
        try {
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp' || file.type === 'image/avif';
            if (!isJpgOrPng) throw new Error('您只能上传JPG/PNG/WEBP/AVIF格式的文件')
            const { width, height } = await Utils.getImageDimensions(file)
            if ((width > imgMaxLimit.current || height > imgMaxLimit.current)) {
                await uploadConfirm('confirm', {
                    title: '图片过大',
                    content: `为您调整尺寸后可打开, 分辨率 ≤ ${imgMaxLimit.current}*${imgMaxLimit.current}`,
                    okText: '调整并打开',
                    cancelText: '重新上传',
                    onOk: async () => {
                        const resizeRes = await Utils.resizeImageFileMaxByAliyunBy(file, imgMaxLimit.current)
                        if (resizeRes && resizeRes.success) {
                            beforeUpload(resizeRes.file)
                        } else {
                            message.warning(resizeRes.message)
                        }
                    },
                    onCancel: async () => { // 上传取消不会关闭弹窗
                        await reupload()
                    }
                })
            } else {
                    if (file) {
                        const imageType = globalHomePageType == HOME_PAGE_TYPE.MERCHANDISE ?
                        IMAGE_TYPE.COMMODITY :
                        IMAGE_TYPE.MODEL
                    const source = imageType == IMAGE_TYPE.MODEL ? UPLOAD_SOURCE.AIEDIT_MODEL : UPLOAD_SOURCE.AIEDIT_COMMODITY
                    const res = await Utils.checkImageIslegal(file, 0, source)
                    if (!res) return false
                    if (!res.isLegal) {
                        PSAIModal.confirm({
                            content: '经检测，您上传的内容涉嫌违规，已进行屏蔽处理，人工审核会对图片进行复核。',
                            okText: '继续上传',
                            cancelText: '取消',
                            onOk() { reupload() },
                            onCancel() { },
                        })
                        return false
                    }
                    clearProjectAssociation()
                    imageUrlRef.current = res.data
                    if (menuKeyRef.current === 'outpaint') {
                        setOutpaintConfig({
                            multiple: 125,
                            ratio: '3:4',
                            generateCount: 1,
                            moveMode: true,
                            resetSeed: Date.now(),
                        })
                    }
                    setAIEditUrl(imageUrlRef.current)
                    // setNeedPay(true)
                }
            }
        } catch (err) {
            message.warning(err.message)
        } finally {
            return false
        }
    }
    const beforeUploadRef = useRef(beforeUpload)
    useEffect(() => {
        beforeUploadRef.current = beforeUpload
    }, [beforeUpload])
    const stableUploadCb = useCallback((...args) => beforeUploadRef.current?.(...args), [])

    const handleImageChange = useCallback((url) => {
        if (!url || imageUrlRef.current === url) return
        clearProjectAssociation()
        imageUrlRef.current = url
        if (menuKeyRef.current === 'outpaint') {
            setOutpaintConfig({
                multiple: 125,
                ratio: '3:4',
                generateCount: 1,
                moveMode: true,
                resetSeed: Date.now(),
            })
        }
        setAIEditUrl(imageUrlRef.current)
        setNeedPay(true)
    }, [clearProjectAssociation, setAIEditUrl, setNeedPay])

    const handleProjectReupload = useCallback((url) => {
        if (!url) return
        clearProjectAssociation()
        imageUrlRef.current = url
        if (menuKeyRef.current === 'outpaint') {
            setOutpaintConfig({
                multiple: 125,
                ratio: '3:4',
                generateCount: 1,
                moveMode: true,
                resetSeed: Date.now(),
            })
        }
        setAIEditUrl(imageUrlRef.current)
        setNeedPay(true)
    }, [clearProjectAssociation, setAIEditUrl, setNeedPay])

    const resetOutpaintConfig = useCallback(() => {
        setOutpaintConfig({
            multiple: 125,
            ratio: '3:4',
            generateCount: 1,
            moveMode: true,
            resetSeed: Date.now(),
        })
    }, [])

    const handleOutpaintApply = useCallback(async () => {
        if (!imageUrlRef.current) return
        const status = Utils.checkLoginStatus(() => handleOutpaintApply())
        if (!status) return

        const layout = outpaintLayoutRef.current
        if (!layout?.coordinate) {
            message.warning('扩图参数尚未准备完成，请稍后重试')
            return
        }

        setAIEditLoading(true)
        try {
            const res = await API.doImageOutpaintingv2({
                imageType: 'imageOutpainting',
                aspectRatio: outpaintConfig.ratio === 'origin' ? 'original' : outpaintConfig.ratio,
                coordinate: layout.coordinate,
                maskByType: {
                    main: {
                        image: imageUrlRef.current,
                    }
                },
                num: outpaintConfig.generateCount || 1,
                projectId: AIEditTemprojectId || projectIdRef.current || '',
                taskId: taskIdRef.current || '',
                upscale: outpaintConfig.multiple,
                attr: {},
            })

            setGlobalTaskReadFlag(prev => !prev)
            if (!res) {
                setAIEditLoading(false)
                return
            }
 
            eventBus.emit('updatePower')
            const tempProjectId = res.data?.id
            const taskId = res.data?.taskList?.[0]
            if (!taskId) {
                setAIEditLoading(false)
                message.error('扩图任务创建失败', 5)
                return
            }
            setSelectedTaskListId(tempProjectId)
            setAIEditTempProjectId(tempProjectId)
            setAIEditRecentTaskId(taskId)
            projectIdRef.current = tempProjectId
            taskIdRef.current = taskId

            const pollTaskStatus = async () => {
                const taskInfo = await API.getTaskInfoV2({ taskId })
                const currentStatus = taskInfo.data?.task?.status

                if (currentStatus !== 'succeeded' && currentStatus !== 'failed') {
                    setTimeout(() => {
                        pollTaskStatus()
                    }, 1000)
                    return
                }

                if (currentStatus === 'succeeded') {
                    eventBus.emit('updatePower')
                    const currentTaskId = taskInfo.data?.task?.taskId
                    taskIdRef.current = currentTaskId
                    setAIEditRecentTaskId(currentTaskId)
                    const noWaterImage = await API.commonDownload({ taskId: currentTaskId })
                    if (!noWaterImage) {
                        setAIEditLoading(false)
                        return
                    }
                    imageUrlRef.current = noWaterImage.data
                    setAIEditUrl(noWaterImage.data)
                    setNeedPay(true)
                    message.success('处理成功', 5)
                    setAIEditLoading(false)
                    return
                }

                eventBus.emit('updatePower')
                message.error('处理失败', 5)
                setAIEditLoading(false)
            }

            pollTaskStatus()
        } catch (err) {
            message.warning(err.message)
            setAIEditLoading(false)
        }
    }, [AIEditTemprojectId, outpaintConfig.generateCount, outpaintConfig.multiple, outpaintConfig.ratio, setAIEditLoading, setAIEditRecentTaskId, setAIEditTempProjectId, setAIEditUrl, setGlobalTaskReadFlag, setNeedPay, setSelectedTaskListId])

    const getMultiSemanticOutput = (output) => {
        setOutputMask(output)
    }

    const uploadPlaceholderConfig = useMemo(() => ({
        matting: {
            title: '智能抠图',
            placeholderCards: [
                { title: 'AI 智能识别, 精准抠出图中商品', image: `${AI_EDIT_UPLOAD_BG}matting1.png` },
                { title: 'AI 智能识别, 精准抠出图中模特', image: `${AI_EDIT_UPLOAD_BG}matting2.png` },
            ]
        },
        filllight: {
            title: '智能补光',
            placeholderCards: [
                { title: '全局模式', subtitle: '优化全图光影', image: `${AI_EDIT_UPLOAD_BG}filllight1.png` },
                { title: '人像模式', subtitle: '优化人像光照', image: `${AI_EDIT_UPLOAD_BG}filllight2.png` },
                { title: '反光板模式', subtitle: '模拟反光板效果, 人像光照更柔和', image: `${AI_EDIT_UPLOAD_BG}filllight3.png` },
            ]
        },
        beautify: {
            title: '模特美化',
            placeholderCards: [
                { title: '服装模特一键美化', image: '/static/images/aiEdit/beauty.png' },
            ]
        },
        magicErase: {
            title: '魔法擦除',
            placeholderCards: [
                { title: '一键擦除画面杂乱元素', image: `${AI_EDIT_UPLOAD_BG}magicErase.png` },
            ]
        },
        handRepair: {
            title: '手部修复',
            placeholderCards: [
                { title: '一键修复AI错手, 图片更真实', image: `${AI_EDIT_UPLOAD_BG}handRepair.png` },
            ]
        },
        outpaint: {
            title: 'AI扩图',
            placeholderCards: [
                { title: '一键扩展画面，AI智能补全场景', image: `${AI_EDIT_UPLOAD_BG}expansion.png` },
            ]
        },
        imageFiner: {
            title: 'AI超清',
            placeholderCards: [
                { title: 'AI智能修复模糊图片, 一键生成超清画质', image: `${AI_EDIT_UPLOAD_BG}imageFiner.png` },
            ]
        },
        imageResize: {
            title: '高清改尺寸',
            placeholderCards: [
                { title: '一键修改图片尺寸, 完美适配不同平台', image: `${AI_EDIT_UPLOAD_BG}imageFiner.png` },
            ]
        }
    }), [])

    const currentUploadConfig = uploadPlaceholderConfig[menuKey] || uploadPlaceholderConfig.matting

    const defaultPage = (
        <WaitUploadPage
            btnText={globalHomePageType == HOME_PAGE_TYPE.MERCHANDISE ? "上传商品图" : "上传模特图"}
            uploadTips={`请点击上方按钮添加图片或将图片拖拽至此区域内上传
500*500≤上传图片尺寸≤${imgMaxLimit.current}*${imgMaxLimit.current}`}
            uploadExamples={modelExamples}
            getMultiSemanticOutput={getMultiSemanticOutput}
            pickFromProject={true}
            onUpload={beforeUpload}
            onChange={handleImageChange}
            useImageType
            title={currentUploadConfig.title}
            placeholderCards={currentUploadConfig.placeholderCards}
        />
    )

    // 调用各模块内部方法
    const oneClickFaceBeauty = () => {
        beautifyRef.current?.oneClickFaceBeauty()
    }
    const oneClickBodyBeauty = () => {
        beautifyRef.current?.oneClickBodyBeauty()
    }
    const saveUndoList = () => {
        beautifyRef.current?.saveUndoList()
    }
    const [faceArray, setFaceArray] = useState()
    const [bodyArray, setBodyArray] = useState()

    const handleDraw = (key) => {
        if (menuKey == 'magicErase') magicEraseRef.current.handleDraw(key)
        else if (menuKey == 'handRepair') handRepairRef.current.handleDraw(key)
    }

    const repairSizeChange = (value, text) => {
        if (menuKey == 'magicErase') magicEraseRef.current.repairSizeChange(value, text)
        else if (menuKey == 'handRepair') handRepairRef.current.repairSizeChange(value, text)
    }

    const createClick = (value) => {
        const status = Utils.checkLoginStatus(() => createClick())
        if (!status) return
        if (menuKey == 'magicErase') {
            magicEraseRef.current.createClick(value)
        } else if (menuKey == 'handRepair') {
            handRepairRef.current.createClick(value)
        } else if (menuKey == 'filllight') {
            filllightRef.current.createClick(value)
        } else if (menuKey == 'imageFiner') {
            imageFinerRef.current.createClick(value)
        }
    }

    const createClickBlur = (zoom) => {
        imageUpgradeRef.current.createClick(zoom)
    }

    const resetClick = () => {
    }

    // 保存结果图到项目
    const saveResult = () => {
        if (menuKey == 'beautify') {
            beautifyRef.current?.saveResult()
        } else if (menuKey == 'filllight') {
            filllightRef.current.saveResult()
        } else if (menuKey == 'imageFiner') {
            imageFinerRef.current.saveResult()
        }
    }

    // 图片更换后清空临时项目id
    useEffect(() => {
        setAIEditTempProjectId()
        setAIEditLoading(false)
    }, [imageUrlRef.current])

    // 清空
    const handleCleanClick = () => {
        clearProjectAssociation()
        imageUrlRef.current = null
        setAIEditUrl(null)
        setBottomConfig(null)
        resetOutpaintConfig()
    }

    const EDIT_CONT_MAP = {
        [MENU_ITEMS?.BEAUTIFY.key]: <AIBeauty onBottomConfigChange={setBottomConfig} handleCleanClick={handleCleanClick} getfaceArray={setFaceArray} getbodyArray={setBodyArray} ref={beautifyRef} key={imageUrlRef.current} projectId={projectIdRef.current} taskId={taskIdRef.current} url={imageUrlRef.current} defaultPage={defaultPage} uploadCb={stableUploadCb} errorCallback={errorCallback} />,
        [MENU_ITEMS?.MAGIC_ERASE.key]: <MagicErase onBottomConfigChange={setBottomConfig} handleCleanClick={handleCleanClick} ref={magicEraseRef} key={imageUrlRef.current} projectId={projectIdRef.current} taskId={taskIdRef.current} url={imageUrlRef.current} defaultPage={defaultPage} uploadCb={stableUploadCb} />,
        [MENU_ITEMS?.HAND_REPAIR.key]: <HandRepair onBottomConfigChange={setBottomConfig} handleCleanClick={handleCleanClick} ref={handRepairRef} key={imageUrlRef.current} projectId={projectIdRef.current} taskId={taskIdRef.current} url={imageUrlRef.current} defaultPage={defaultPage} uploadCb={stableUploadCb} />,
        [MENU_ITEMS?.FILL_LIGHT.key]: <FillLight onBottomConfigChange={setBottomConfig} handleCleanClick={handleCleanClick} ref={filllightRef} key={imageUrlRef.current} projectId={projectIdRef.current} taskId={taskIdRef.current} url={imageUrlRef.current} defaultPage={defaultPage} uploadCb={stableUploadCb} errorCallback={errorCallback} />,
        [MENU_ITEMS?.IMAGE_FINER.key]: <ImageFiner onBottomConfigChange={setBottomConfig} handleCleanClick={handleCleanClick} ref={imageFinerRef} key={imageUrlRef.current} projectId={projectIdRef.current} taskId={taskIdRef.current} url={imageUrlRef.current} defaultPage={defaultPage} uploadCb={stableUploadCb} />, 
        [MENU_ITEMS?.OUTPAINT.key]: <Outpaint onBottomConfigChange={setBottomConfig} handleCleanClick={handleCleanClick} key={imageUrlRef.current} url={imageUrlRef.current} taskId={taskIdRef.current} defaultPage={defaultPage} uploadCb={stableUploadCb} onProjectReupload={handleProjectReupload} multiple={outpaintConfig.multiple} ratio={outpaintConfig.ratio} moveMode={outpaintConfig.moveMode} resetSeed={outpaintConfig.resetSeed} onToggleMoveMode={() => setOutpaintConfig(prev => ({ ...prev, moveMode: !prev.moveMode }))} onReset={resetOutpaintConfig} onLayoutChange={(layout) => { outpaintLayoutRef.current = layout }} />,
        [MENU_ITEMS?.MATTING.key]: <FillLight onBottomConfigChange={setBottomConfig} handleCleanClick={handleCleanClick} ref={filllightRef} key={imageUrlRef.current} projectId={projectIdRef.current} taskId={taskIdRef.current} url={imageUrlRef.current} defaultPage={defaultPage} uploadCb={stableUploadCb} errorCallback={errorCallback} />,
    }
    const hasImage = !!imageUrlRef.current
    const bottomLoading = hasImage && !bottomConfig
    const isImageResize = menuKey === MENU_ITEMS?.IMAGE_RESIZE.key
    const enableProjectReupload = ['beautify', 'filllight', 'imageFiner', 'magicErase', 'handRepair', 'outpaint'].includes(menuKey)
    const showBottomPlaceholder = hasImage
    const imageResizeContent = (
        <>
            {hasImage && (
                <RightOperatePanel
                    activeKey={menuKey}
                    url={imageUrlRef.current}
                    handleDraw={handleDraw}
                    repairSizeChange={repairSizeChange}
                    createClick={createClick}
                    saveResult={saveResult}
                    oneClickFaceBeauty={oneClickFaceBeauty}
                    oneClickBodyBeauty={oneClickBodyBeauty}
                    saveUndoList={saveUndoList}
                    faceArray={faceArray}
                    bodyArray={bodyArray}
                    imageResizeLeftRef={imageResizeLeftRef}
                    boundaryRef={rightMainRef}
                    outpaintConfig={outpaintConfig}
                    onOutpaintMultipleChange={(multiple) => setOutpaintConfig(prev => ({ ...prev, multiple }))}
                    onOutpaintRatioChange={(ratio) => setOutpaintConfig(prev => ({ ...prev, ratio }))}
                    onOutpaintApply={handleOutpaintApply}
                />
            )}
            <div className={`${styles.CanvasArea} ${!hasImage ? styles.CanvasAreaFull : ''}`}>
                <ResizePageRight
                    onBottomConfigChange={setBottomConfig}
                    handleCleanClick={handleCleanClick}
                    reUpload={() => imageResizeLeftRef.current?.resetResolution?.()}
                />
            </div>
        </>
    )
    const rightTopContent = (
        <>
            {hasImage && (
                <RightOperatePanel
                    activeKey={menuKey}
                    url={imageUrlRef.current}
                    handleDraw={handleDraw}
                    repairSizeChange={repairSizeChange}
                    createClick={createClick}
                    saveResult={saveResult}
                    oneClickFaceBeauty={oneClickFaceBeauty}
                    oneClickBodyBeauty={oneClickBodyBeauty}
                    saveUndoList={saveUndoList}
                    faceArray={faceArray}
                    bodyArray={bodyArray}
                    imageResizeLeftRef={imageResizeLeftRef}
                    boundaryRef={rightMainRef}
                    outpaintConfig={outpaintConfig}
                    onOutpaintMultipleChange={(multiple) => setOutpaintConfig(prev => ({ ...prev, multiple }))}
                    onOutpaintRatioChange={(ratio) => setOutpaintConfig(prev => ({ ...prev, ratio }))}
                    onOutpaintApply={handleOutpaintApply}
                />
            )}
            <div className={`${styles.CanvasArea} ${!hasImage ? styles.CanvasAreaFull : ''}`}>
                {EDIT_CONT_MAP[menuKey]}
            </div>
        </>
    )

    return (
        <Spin spinning={loading} delay={500} wrapperClassName={styles.Spin}>
            <div className={styles.main_style}>
                <EditLeftBar
                    activeKey={menuKey}
                    url={imageUrlRef.current}
                    handleDraw={handleDraw}
                    repairSizeChange={repairSizeChange}
                    createClick={createClick}
                    createClickBlur={createClickBlur}
                    imageUrlRef={imageUrlRef.current}
                    onClick={onClick}
                    resetClick={resetClick}
                    setOutputMask={setOutputMask}
                    saveResult={saveResult}
                />
                <div className={styles.RightMain} ref={rightMainRef}>
                    <div className={styles.RightTop}>
                        {isImageResize ? (
                            <ImageResizeContextProvider
                                initialImageUrl={imageUrlRef.current}
                                initialProjectId={projectIdRef.current}
                                initialTaskId={taskIdRef.current}
                                onImageChange={handleImageChange}
                            >
                                {imageResizeContent}
                            </ImageResizeContextProvider>
                        ) : rightTopContent}
                    </div>
                    {showBottomPlaceholder && (
                        <BottomPlaceholder
                            visible={showBottomPlaceholder}
                            loading={bottomLoading}
                            config={bottomConfig}
                            enableProjectReupload={enableProjectReupload}
                            onProjectImageChange={handleProjectReupload}
                        />
                    )}
                </div>
                <RightComp imageType={IMAGE_TYPE.AIEDIT} />
            </div>
        </Spin>
    )
}

export default () => (
    <AIEditContextProvider>
        <AiEdit />
    </AIEditContextProvider>
)
